import { useCallback, useEffect, useRef, useState } from 'react';
import { readAndroidConfig, loadAndroidConfigJobs, startAndroidConfig, loadAndroidConfigJob, findAndroidConfigRequest } from './siteControlApi';

const pending = (job) => ['queued', 'running'].includes(job?.status);
const messages = {
  android_config_migration_required: 'Android Config needs a server database update.',
  no_eligible_adb_relay: 'No updated Android relay is available. Check the relay connection and app version.',
  adb_target_not_authorized: 'Verify this device’s ADB connection in Pairing.',
  adb_restart_scope_changed: 'Connection details changed. Refresh device status before trying again.',
  device_config_target_busy: 'Another device operation is active or its delivery is uncertain. Wait for its result.',
  android_config_invalid_name: 'Enter a device name of up to 160 characters without control characters.',
  android_config_invalid_wifi: 'Check the Wi-Fi name, security type, and password. WPA2 needs 8–63 characters or a 64-digit hexadecimal key.',
  android_config_invalid_settings: 'Choose at least one valid setting.',
  android_config_unsupported: 'This firmware does not support the requested Android command.',
  adb_wireless_recovery_unsupported: 'Update an eligible relay’s Suitestream app to set up Wireless Debugging recovery.',
  wireless_debugging_recovery_requires_paired_tls: 'Recovery setup requires a verified paired Wireless Debugging connection.',
};
const failure = (error) => messages[error?.response?.data?.status] || 'Device status could not be read. Check its connection and try Refresh status.';
function requestKey() {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function useAndroidConfig({ siteId, target, active, fresh, onRenamed }) {
  const [config, setConfig] = useState(null);
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [lostRequest, setLostRequest] = useState(null);
  const [pollError, setPollError] = useState('');
  const [observedAt, setObservedAt] = useState(null);
  const mounted = useRef(true);
  const changing = useRef(false);
  const reading = useRef(false);
  const generation = useRef(0);
  const handledRename = useRef(null);
  const latestJob = useRef(null);
  const renameCallback = useRef(onRenamed);
  renameCallback.current = onRenamed;
  const currentScope = useRef(target);
  currentScope.current = target;
  const deviceId = target?.deviceId;
  const capable = target?.androidConfigAvailable === true;
  useEffect(() => () => { mounted.current = false; generation.current++; }, []);

  const acceptJob = useCallback((next, notifyRename = false) => {
    if (!mounted.current || !next || next.targetDeviceId !== deviceId) return;
    if (latestJob.current && latestJob.current.createdAtEpochMs > next.createdAtEpochMs) return;
    latestJob.current = next;
    setJob(next);
    const current = currentScope.current;
    const same = current && next.identityGeneration === current.identityGeneration
      && next.endpointRevision === current.endpointRevision && next.authMode === current.authMode;
    if (notifyRename && same && next.kind === 'rename' && next.nameSaved && next.androidConfig?.deviceName && handledRename.current !== next.id) {
      handledRename.current = next.id;
      setConfig((previous) => ({ ...previous, deviceName: next.androidConfig.deviceName }));
      renameCallback.current?.(next.androidConfig.deviceName);
    }
  }, [deviceId]);

  const refresh = useCallback(async () => {
    if (!deviceId || !capable || reading.current || changing.current) return;
    reading.current = true;
    const revision = ++generation.current;
    setLoading(true); setError('');
    try {
      const response = await readAndroidConfig(siteId, deviceId);
      if (!mounted.current || revision !== generation.current) return;
      setAvailable(response?.available === true);
      if (response?.available) { setConfig(response.androidConfig); setObservedAt(response.observedAtEpochMs); }
      else setError(messages[response?.reason] || 'Android Config is currently unavailable.');
      if (response?.jobs?.[0]) acceptJob(response.jobs[0]);
    } catch (err) {
      if (mounted.current && revision === generation.current) { setError(failure(err)); setAvailable(false); }
    } finally {
      reading.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [siteId, deviceId, capable, acceptJob]);

  useEffect(() => {
    if (!active || !deviceId) return undefined;
    let cancelled = false;
    loadAndroidConfigJobs(siteId, deviceId).then((response) => {
      if (cancelled || !mounted.current) return;
      if (response?.jobs?.[0]) acceptJob(response.jobs[0]);
      if (response?.available === false) setError(messages[response.reason] || 'Android Config is currently unavailable.');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [active, siteId, deviceId, acceptJob]);
  useEffect(() => { if (active && capable) refresh(); }, [active, capable, refresh]);

  useEffect(() => {
    if (!job || !pending(job)) return undefined;
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const result = await loadAndroidConfigJob(siteId, deviceId, job.id);
        if (cancelled || !mounted.current) return;
        if (result?.job?.id !== job.id || result.job.targetDeviceId !== deviceId) throw new Error('invalid_saved_result');
        setPollError(''); acceptJob(result.job, true);
        if (!pending(result.job)) return;
      } catch (_) {
        if (cancelled || !mounted.current) return;
        setPollError('The saved result could not be refreshed. This request will not be sent again.');
      }
      timer = setTimeout(poll, 2000);
    };
    timer = setTimeout(poll, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [siteId, deviceId, job?.id, job?.status, acceptJob]);
  useEffect(() => {
    if (!job?.blocksRestart || pending(job)) return undefined;
    const timer = setTimeout(() => setJob((current) => current?.id === job.id ? { ...current, blocksRestart: false } : current),
      Math.max(0, job.deadlineEpochMs - Date.now()));
    return () => clearTimeout(timer);
  }, [job?.id, job?.status, job?.blocksRestart, job?.deadlineEpochMs]);

  const blocked = pending(job) || (job?.blocksRestart && job.deadlineEpochMs > Date.now());
  const canChange = Boolean(fresh && capable && available && !loading && !submitting && !blocked && !lostRequest);
  const submit = async (change) => {
    if (!canChange || changing.current) return false;
    changing.current = true; generation.current++;
    const key = requestKey();
    setSubmitting(true); setError('');
    try {
      const result = await startAndroidConfig(siteId, deviceId, { confirmed: true, requestKey: key,
        expected: { identityGeneration: target.identityGeneration, endpointRevision: target.endpointRevision, authMode: target.authMode }, change });
      if (!result?.success || !result.job || result.job.targetDeviceId !== deviceId) throw new Error('missing_saved_job');
      if (mounted.current) acceptJob(result.job, true);
      return true;
    } catch (err) {
      if (!mounted.current) return false;
      if (messages[err?.response?.data?.status] || (err?.response?.status && err.response.status < 500)) setError(failure(err));
      else {
        setLostRequest({ key, checkAfter: Date.now() + 11000 });
        setError('The response was not received. Check the saved request before starting another operation; it will not be resent.');
      }
      return false;
    } finally { changing.current = false; if (mounted.current) setSubmitting(false); }
  };
  const recover = async () => {
    if (!lostRequest || changing.current) return;
    changing.current = true; setSubmitting(true);
    try {
      const result = await findAndroidConfigRequest(siteId, deviceId, lostRequest.key);
      if (result?.job?.targetDeviceId !== deviceId) throw new Error('invalid_saved_result');
      if (mounted.current) { acceptJob(result.job, true); setLostRequest(null); setError(''); }
    } catch (err) {
      if (mounted.current && err?.response?.status === 404) {
        if (Date.now() < lostRequest.checkAfter) setError('The request may still be recording. Wait a moment and check the saved request again.');
        else { setLostRequest(null); setError('No saved request was found. You can submit a new request.'); }
      }
    } finally { changing.current = false; if (mounted.current) setSubmitting(false); }
  };
  return { config, job, error, pollError, lostRequest, canChange, loading, submitting, observedAt, refresh, submit, recover };
}
